import type { PortfolioCollectionListItem, RiskModel } from "@repo/shared/types";
import { Pencil, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { AlertMessage } from "@/components/ui/alert-message";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Switch } from "@/components/ui/switch";
import {
  rowControl,
  Table,
  TableBody,
  TableCell,
  TableCellLink,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import * as pcApi from "@/lib/api/portfolio-collections";
import { useDeleteCheck } from "@/lib/hooks/use-delete-check";
import {
  useDeleteCollection,
  useToggleCollectionActive,
} from "@/lib/hooks/use-portfolio-collections";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { PortfolioCollectionDialog } from "./portfolio-collection-dialog";
import { T } from "./portfolio-collections-table.i18n";

type PortfolioCollectionsTableProps = {
  collections: PortfolioCollectionListItem[];
  riskModels: RiskModel[];
};

export function PortfolioCollectionsTable({
  collections,
  riskModels,
}: PortfolioCollectionsTableProps) {
  const localize = useLocalize();
  const t = useT(T);

  const deleteCollection = useDeleteCollection();
  const toggleActive = useToggleCollectionActive();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRiskModel, setSelectedRiskModel] = useState<string>("");
  const [editingCollection, setEditingCollection] = useState<PortfolioCollectionListItem | null>(
    null,
  );
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [collectionToDelete, setCollectionToDelete] = useState<string | null>(null);
  // What the delete also removes (e.g. dynamic portfolios), from the server check.
  const [deleteNotice, setDeleteNotice] = useState<string | null>(null);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    title: "",
    description: "",
    variant: "default" as "default" | "destructive",
  });

  const showAlert = (
    title: string,
    description: string,
    variant: "default" | "destructive" = "default",
  ) => {
    setAlertConfig({ title, description, variant });
    setAlertOpen(true);
  };

  // QL-168 — a collection the server would refuse to delete shows why on the
  // Delete click, before the confirmation.
  const { requestDelete, checkingId } = useDeleteCheck({
    check: pcApi.checkDelete,
    onAllowed: (id, notice) => {
      setCollectionToDelete(id);
      setDeleteNotice(notice);
      setConfirmDeleteOpen(true);
    },
    onBlocked: (reason) => showAlert(t.error, reason || t.failedToDeleteCollection, "destructive"),
  });

  const handleDelete = async () => {
    if (!collectionToDelete) return;

    deleteCollection.mutate(collectionToDelete, {
      onSuccess: () => {
        // Transient confirmation — a toast, not a blocking alert (errors keep
        // the alert: a failure deserves blocking attention).
        toast.success(t.collectionDeletedSuccess);
      },
      onError: (err) => {
        showAlert(t.error, err.message || t.failedToDeleteCollection, "destructive");
      },
    });
    setCollectionToDelete(null);
  };

  const handleToggleActive = (collectionId: string) => {
    toggleActive.mutate(collectionId, {
      onError: (err) => {
        showAlert(t.error, err.message || t.failedToToggleActive, "destructive");
      },
    });
  };

  return (
    <>
      <div className="space-y-4">
        {/* Add Collection button */}
        <div className="flex justify-end">
          <Button
            onClick={() => {
              setSelectedRiskModel("");
              setEditingCollection(null);
              setDialogOpen(true);
            }}
          >
            {t.addPortfolioCollection}
          </Button>
        </div>

        {/* All collections in one table */}

        {collections.length === 0 ? (
          <EmptyState>{t.noCollections}</EmptyState>
        ) : (
          <Table variant="navigation">
            <TableHeader>
              <TableRow>
                <TableHead>{t.name}</TableHead>
                <TableHead>{t.riskModel}</TableHead>
                <TableHead>{t.portfolios}</TableHead>
                <TableHead>{t.active}</TableHead>
                <TableHead className="text-right">{t.actions}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {collections.map((collection) => (
                <TableRow key={collection.id}>
                  <TableCellLink
                    className="font-medium"
                    to={`/settings/portfolios/${collection.id}`}
                  >
                    {localize(collection.nameI18n)}
                  </TableCellLink>
                  <TableCell>
                    {collection.riskModel ? (
                      <span>{localize(collection.riskModel.nameI18n)}</span>
                    ) : (
                      <span className="text-muted-foreground">{t.none}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="text-muted-foreground">
                      {collection.portfolios.length}{" "}
                      {collection.portfolios.length === 1 ? t.portfolio : t.portfoliosPlural}
                    </span>
                  </TableCell>
                  <TableCell className={rowControl}>
                    <Switch
                      aria-label={`${t.active}: ${localize(collection.nameI18n)}`}
                      checked={collection.isActive}
                      onCheckedChange={() => handleToggleActive(collection.id)}
                    />
                  </TableCell>
                  <TableCell className={cn("text-right", rowControl)}>
                    <div className="flex items-center justify-end">
                      <Button
                        size="icon"
                        tooltip={t.edit}
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingCollection(collection);
                          setDialogOpen(true);
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="icon"
                        tooltip={t.delete}
                        variant="ghost"
                        loading={checkingId === collection.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          requestDelete(collection.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <PortfolioCollectionDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setEditingCollection(null);
          }
        }}
        riskModelId={selectedRiskModel}
        riskModels={riskModels}
        collection={editingCollection}
      />

      <ConfirmDialog
        open={confirmDeleteOpen}
        onOpenChange={setConfirmDeleteOpen}
        title={t.deleteCollection}
        description={
          deleteNotice
            ? `${t.deleteCollectionDescription} ${deleteNotice}`
            : t.deleteCollectionDescription
        }
        confirmText={t.delete}
        cancelText={t.cancel}
        onConfirm={handleDelete}
        variant="destructive"
      />

      <AlertMessage
        open={alertOpen}
        onOpenChange={setAlertOpen}
        title={alertConfig.title}
        description={alertConfig.description}
        variant={alertConfig.variant}
      />
    </>
  );
}
